import React from "react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { useMyRegistrations } from "../../hooks/useMyRegistrations";
import MyRegistrationCard from "../MyRegistrationCard/MyRegistrationCard";
import { MyRegistrationItem } from "../../api/getMyRegistrations/types";
import { Link } from "react-router-dom";
import permissionsService from "@/services/permissionsService";

function RegistrationList({
  items,
  emptyMessage,
  canManageRegistrations,
  onCancelSuccess,
}: {
  items: MyRegistrationItem[];
  emptyMessage: React.ReactNode;
  canManageRegistrations: boolean;
  onCancelSuccess: () => void;
}) {
  if (items.length === 0) {
    return (
      <div className="py-16 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {emptyMessage}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((r) => (
        <MyRegistrationCard
          key={r.sessionUuid}
          registration={r}
          canManageRegistrations={canManageRegistrations}
          onCancelSuccess={onCancelSuccess}
        />
      ))}
    </div>
  );
}

const MyRegistrationsPage: React.FC = () => {
  const { upcoming, past, loading, error, refetch } = useMyRegistrations();
  const { hasPermission } = permissionsService();
  const canManageRegistrations = hasPermission("event-registration");

  return (
    <MainPageWrapper title="My Registrations" loading={loading}>
      {error && (
        <div className="py-16 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">{error}</p>
        </div>
      )}

      {!error && !loading && (
        <Tabs defaultValue="upcoming">
          <TabsList>
            <TabsTrigger value="upcoming">
              Upcoming{upcoming.length > 0 ? ` (${upcoming.length})` : ""}
            </TabsTrigger>
            <TabsTrigger value="past">
              Past{past.length > 0 ? ` (${past.length})` : ""}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming">
            <RegistrationList
              items={upcoming}
              canManageRegistrations={canManageRegistrations}
              onCancelSuccess={refetch}
              emptyMessage={
                <>
                  No upcoming registrations.{" "}
                  <Link
                    to="/event-registration/events"
                    className="text-gray-900 dark:text-white underline"
                  >
                    Browse events
                  </Link>
                </>
              }
            />
          </TabsContent>

          <TabsContent value="past">
            <RegistrationList
              items={past}
              canManageRegistrations={canManageRegistrations}
              onCancelSuccess={refetch}
              emptyMessage="No past registrations."
            />
          </TabsContent>
        </Tabs>
      )}
    </MainPageWrapper>
  );
};

export default MyRegistrationsPage;
